import { createSlice } from '@reduxjs/toolkit';

import { POLYGON_SEGMENT } from 'common/components/segment/constants';

import {
    ADD_SEGMENT_MODAL_OPEN_STATUS,
    ADD_SEGMENT_MODAL_EDITION_MODE,
    CHANGE_MODULE_STATUS,
    CHANGE_MODULE_STATUS_FAILURE,
    CHANGE_MODULE_STATUS_SUCCESS,
    CHECK_SEGMENT,
    CHECK_SEGMENT_FAILURE,
    CHECK_SEGMENT_SUCCESS,
    DELETE_SEGMENT,
    DELETE_SEGMENT_FAILURE,
    DELETE_SEGMENT_SUCCESS,
    EDIT_SEGMENT,
    EDIT_SEGMENT_FAILURE,
    EDIT_SEGMENT_SUCCESS,
    ENABLED_EDITION,
    ENABLED_EDITION_FAILURE,
    ENABLED_EDITION_SUCCESS,
    FETCH_OFFER_INFO_PATERN,
    FETCH_OFFER_INFO_PATERN_FAILURE,
    FETCH_OFFER_INFO_PATERN_SUCCESS,
    FETCH_OFFER_PANELS,
    FETCH_OFFER_PANELS_FAILURE,
    FETCH_OFFER_PANELS_SUCCESS,
    FETCH_PARENT_FIELD_SEGMENT,
    FETCH_PARENT_FIELD_SEGMENT_FAILURE,
    FETCH_PARENT_FIELD_SEGMENT_SUCCESS,
    FETCH_PRESET,
    FETCH_PRESET_FAILURE,
    FETCH_PRESET_SUCCESS,
    GENERATE_PROPOSAL,
    GENERATE_PROPOSAL_FAILURE,
    GENERATE_PROPOSAL_SUCCESS,
    INITIALIZE_SHADER_SETTINGS_VALUES,
    INITIALIZE_VALUES,
    RESET_FORM,
    RESET_VALUES,
    SAVE_IMAGE,
    SAVE_IMAGE_FAILURE,
    SAVE_IMAGE_SUCCESS,
    SAVE_SEGMENT,
    SAVE_SEGMENT_FAILURE,
    SAVE_SEGMENT_SUCCESS,
    SAVE_SHADER_SETTINGS_SUCCESS,
    SELECT_SEGMENT,
    SET_COPY_BUFFER,
    SET_DRAWING_MODE,
    SET_DRAWING_TYPE,
    SET_IS_DISABLED_EDITION,
    SET_IS_DISABLING_MODULES,
    SET_NEW_SEGMENT_CONFIG,
    SET_SELECTED_ITEMS,
    SET_VIEW_LAYER_STATUS,
    SETUP_SIMULATION,
    UPDATE_SEGMENTS,
    UPDATE_SOLAR_MODULES_DESIGN_NUMBER,
} from './actionTypes';
import { NAME } from './constants';

const datetime = new Date();

const SHADER_SETTINGS_DEFAULT_VALUES = {
    date: `${datetime.getDate()}/${
        datetime.getMonth() + 1
    }/${datetime.getFullYear()}`,
    day: datetime.getDate(),
    hour: datetime.getHours(),
    minutes: datetime.getMinutes(),
    month: datetime.getMonth() + 1,
    timezone: `${(-1 * datetime.getTimezoneOffset()) / 60}`,
    year: datetime.getFullYear(),
};

const INITIAL_STATE = {
    addSegmentModalOpenStatus: false,
    addSegmentModalEditionMode: { isEditionMode: false },
    changeModuleStatus: { errors: null, isSaving: false },
    checkSegment: { data: {}, errors: null, isSaving: false },
    copyBuffer: [],
    createSegment: { data: {}, errors: null, isSaving: false },
    currentProjectLocation: { data: {} },
    deleteSegment: { errors: null, isDeleting: false },
    drawingMode: false,
    drawingType: POLYGON_SEGMENT,
    enableEdition: { errors: [], isSaving: false },
    fetchParentFieldSegment: {
        data: {
            location: null,
            segments: [],
            solarModulesDesignNumber: 0,
            solarModulesOfferNumber: 0,
        },
        errors: [],
        isFetching: false,
    },
    fetchPreset: { data: {}, errors: [], isFetching: false },
    generateProposal: { errors: [], isFetching: false },
    initialValues: {},
    isDisabledEdition: false,
    isDisablingModules: false,
    newSegmentConfig: {},
    offerInfoPatern: { data: {}, errors: [], isFetching: false },
    offerPanels: {
        counters: { assigned: 0, available: 0 },
        data: [],
        errors: [],
        isFetching: true,
    },
    saveImage: { data: {}, errors: [], isSaving: false },
    saveShaderSettings: {
        data: SHADER_SETTINGS_DEFAULT_VALUES,
        errors: null,
        isSaving: false,
    },
    selectedItems: [],
    selectedSegment: null,
    shaderSettingsInitialValues: SHADER_SETTINGS_DEFAULT_VALUES,
    simulation: { interval: null, isPlaying: false },
    updateSegment: { data: {}, errors: null, isSaving: false },
    viewLayerStatus: true,
};

const panelLayoutV2Slice = createSlice({
    name: NAME,
    initialState: INITIAL_STATE,
    reducers: {
        [ADD_SEGMENT_MODAL_OPEN_STATUS]: (state, action) => {
            state.addSegmentModalOpenStatus = action.payload;
        },
        [ADD_SEGMENT_MODAL_EDITION_MODE]: (state, action) => {
            state.addSegmentModalEditionMode = action.payload;
        },
        [CHANGE_MODULE_STATUS]: (state) => {
            state.changeModuleStatus.isSaving = true;
        },
        [CHANGE_MODULE_STATUS_FAILURE]: (state, action) => {
            state.changeModuleStatus = {
                ...state.changeModuleStatus,
                errors: action.payload,
                isSaving: false,
            };
        },
        [CHANGE_MODULE_STATUS_SUCCESS]: (state, action) => {
            state.changeModuleStatus = {
                ...state.changeModuleStatus,
                data: action.payload,
                errors: [],
                isSaving: false,
            };
        },
        [CHECK_SEGMENT]: (state) => {
            state.checkSegment.isSaving = true;
        },
        [CHECK_SEGMENT_FAILURE]: (state, action) => {
            state.checkSegment = {
                ...state.checkSegment,
                errors: action.payload,
                isSaving: false,
            };
        },
        [CHECK_SEGMENT_SUCCESS]: (state, action) => {
            state.checkSegment = {
                ...state.checkSegment,
                data: action.payload,
                errors: [],
                isSaving: false,
            };
        },
        [DELETE_SEGMENT]: (state) => {
            state.deleteSegment.isDeleting = true;
        },
        [DELETE_SEGMENT_FAILURE]: (state, action) => {
            state.deleteSegment = {
                ...state.deleteSegment,
                errors: action.payload,
                isDeleting: false,
            };
        },
        [DELETE_SEGMENT_SUCCESS]: (state) => {
            state.selectedSegment = null;
            state.deleteSegment = {
                ...state.deleteSegment,
                errors: [],
                isDeleting: false,
            };
        },
        [EDIT_SEGMENT]: (state) => {
            state.updateSegment.isSaving = true;
        },
        [EDIT_SEGMENT_FAILURE]: (state, action) => {
            state.updateSegment = {
                ...state.updateSegment,
                errors: action.payload,
                isSaving: false,
            };
        },
        [EDIT_SEGMENT_SUCCESS]: (state, action) => {
            state.updateSegment = {
                ...state.updateSegment,
                data: action.payload,
                errors: [],
                isSaving: false,
            };
        },
        [ENABLED_EDITION]: (state) => {
            state.enableEdition.isSaving = true;
        },
        [ENABLED_EDITION_FAILURE]: (state, action) => {
            state.enableEdition = {
                ...state.enableEdition,
                errors: action.payload,
                isSaving: false,
            };
        },
        [ENABLED_EDITION_SUCCESS]: (state) => {
            state.enableEdition.isSaving = false;
        },
        [FETCH_OFFER_INFO_PATERN]: (state) => {
            state.offerInfoPatern = {
                ...state.offerInfoPatern,
                errors: [],
                isFetching: true,
            };
        },
        [FETCH_OFFER_INFO_PATERN_FAILURE]: (state, action) => {
            state.offerInfoPatern = {
                ...state.offerInfoPatern,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_OFFER_INFO_PATERN_SUCCESS]: (state, action) => {
            state.offerInfoPatern = {
                ...state.offerInfoPatern,
                data: action.payload,
                isFetching: false,
            };
        },
        [FETCH_OFFER_PANELS]: (state) => {
            state.offerPanels = {
                ...state.offerPanels,
                errors: [],
                isFetching: true,
            };
        },
        [FETCH_OFFER_PANELS_FAILURE]: (state, action) => {
            state.offerPanels = {
                ...state.offerPanels,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_OFFER_PANELS_SUCCESS]: (state, action) => {
            state.offerPanels = {
                ...state.offerPanels,
                counters: action.payload.counters,
                data: action.payload.panels,
                isFetching: false,
            };
        },
        [FETCH_PARENT_FIELD_SEGMENT]: (state) => {
            state.fetchParentFieldSegment = {
                ...state.fetchParentFieldSegment,
                errors: [],
                isFetching: true,
            };
        },
        [FETCH_PARENT_FIELD_SEGMENT_FAILURE]: (state, action) => {
            state.fetchParentFieldSegment = {
                ...state.fetchParentFieldSegment,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_PARENT_FIELD_SEGMENT_SUCCESS]: (state, action) => {
            state.fetchParentFieldSegment = {
                ...state.fetchParentFieldSegment,
                data: action.payload,
                isFetching: false,
            };
            state.currentProjectLocation.data = action.payload.location;

            let newShadderSettings = {};

            const newDateTime = new Date();

            newShadderSettings = {
                day: newDateTime.getDate(),
                hour: newDateTime.getHours(),
                minutes: newDateTime.getMinutes(),
                month: newDateTime.getMonth() + 1,
                timezone: `${(-1 * datetime.getTimezoneOffset()) / 60}`,
                year: newDateTime.getFullYear(),
            };

            state.shaderSettingsInitialValues = {
                ...state.shaderSettingsInitialValues,
                id: action.payload.id,
                lat: action.payload.location.latitude,
                lng: action.payload.location.longitude,
                ...newShadderSettings,
            };
            state.saveShaderSettings = {
                ...state.saveShaderSettings,
                data: {
                    ...state.saveShaderSettings.data,
                    id: action.payload.id,
                    lat: action.payload.location.latitude,
                    lng: action.payload.location.longitude,
                    ...newShadderSettings,
                },
            };
        },
        [FETCH_PRESET]: (state) => {
            state.fetchPreset = {
                ...state.fetchPreset,
                errors: [],
                isFetching: true,
            };
        },
        [FETCH_PRESET_FAILURE]: (state, action) => {
            state.fetchPreset = {
                ...state.fetchPreset,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_PRESET_SUCCESS]: (state, action) => {
            state.fetchPreset = {
                ...state.fetchPreset,
                data: action.payload,
                isFetching: false,
            };
        },
        [GENERATE_PROPOSAL]: (state) => {
            state.generateProposal.isFetching = true;
        },
        [GENERATE_PROPOSAL_FAILURE]: (state, action) => {
            state.generateProposal = {
                ...state.generateProposal,
                errors: action.payload,
                isFetching: false,
            };
        },
        [GENERATE_PROPOSAL_SUCCESS]: (state) => {
            state.generateProposal.isFetching = false;
        },
        [INITIALIZE_SHADER_SETTINGS_VALUES]: (state, action) => {
            state.shaderSettingsInitialValues = {
                ...state.shaderSettingsInitialValues,
                ...action.payload,
            };
        },
        [INITIALIZE_VALUES]: (state, action) => {
            state.initialValues = { ...state.initialValues, ...action.payload };
        },
        [RESET_FORM]: (state) => {
            state.initialValues = INITIAL_STATE.initialValues;
        },
        [RESET_VALUES]: () => INITIAL_STATE,
        [SAVE_IMAGE]: (state) => {
            state.saveImage.isSaving = true;
        },
        [SAVE_IMAGE_FAILURE]: (state, action) => {
            state.saveImage = {
                ...state.saveImage,
                errors: action.payload,
                isSaving: false,
            };
        },
        [SAVE_IMAGE_SUCCESS]: (state, action) => {
            state.saveImage = {
                ...state.saveImage,
                data: action.payload,
                errors: [],
                isSaving: false,
            };
        },
        [SAVE_SHADER_SETTINGS_SUCCESS]: (state, action) => {
            state.saveShaderSettings = {
                ...state.saveShaderSettings,
                data: action.payload,
                errors: [],
                isSaving: false,
            };
        },
        [SAVE_SEGMENT]: (state) => {
            state.createSegment.isSaving = true;
        },
        [SAVE_SEGMENT_FAILURE]: (state, action) => {
            state.createSegment = {
                ...state.createSegment,
                errors: action.payload,
                isSaving: false,
            };
        },
        [SAVE_SEGMENT_SUCCESS]: (state, action) => {
            state.drawingMode = false;
            state.createSegment = {
                ...state.createSegment,
                data: action.payload,
                errors: [],
                isSaving: false,
            };
        },
        [SELECT_SEGMENT]: (state, action) => {
            state.selectedSegment = action.payload;
        },
        [SET_COPY_BUFFER]: (state, action) => {
            state.copyBuffer = action.payload;
        },
        [SET_DRAWING_MODE]: (state, action) => {
            state.drawingMode = action.payload;
        },
        [SET_DRAWING_TYPE]: (state, action) => {
            state.drawingType = action.payload;
        },
        [SET_SELECTED_ITEMS]: (state, action) => {
            state.selectedItems = action.payload;
        },
        [UPDATE_SEGMENTS]: (state, action) => {
            state.fetchParentFieldSegment.data.segments = action.payload;
        },
        [UPDATE_SOLAR_MODULES_DESIGN_NUMBER]: (state, action) => {
            state.fetchParentFieldSegment.data = {
                ...state.fetchParentFieldSegment.data,
                solarModulesDesignNumber: action.payload,
            };
        },
        [SET_IS_DISABLED_EDITION]: (state, action) => {
            state.isDisabledEdition = action.payload;
        },
        [SET_IS_DISABLING_MODULES]: (state, action) => {
            state.isDisablingModules = action.payload;
        },
        [SET_NEW_SEGMENT_CONFIG]: (state, action) => {
            state.newSegmentConfig = action.payload;
        },
        [SETUP_SIMULATION]: (state, action) => {
            state.simulation = { ...state.simulation, ...action.payload };
        },
        [SET_VIEW_LAYER_STATUS]: (state, action) => {
            state.viewLayerStatus = action.payload;
        },
    },
});

export const panelLayoutV2Actions = panelLayoutV2Slice.actions;

export default panelLayoutV2Slice.reducer;
